import axios, { HttpStatusCode } from 'axios';

import { STALE_TIME_5_MIN } from '~/domains/common/constants/query';
import {
  getCarouselContests,
  type GetCarouselContestsPayload,
} from '~/services/contests/contests.service.api';
import type {
  PromotedContestCarousel,
  PromotedContestTab,
} from '~/services/marketing/promoted-contests.schema';

import {
  CONDENSED_CAROUSELS_CACHE_TIME,
  CondensedCarouselsQueryKeys,
  RAIL_RETRY_DELAY_MS,
  RAIL_RETRY_LIMIT,
  type HomeCollectionSlug,
} from '../constants/query';
import { NEXT_BATCH_SIZE } from '../constants/rail';

import { getNextBatchOffset, orderByContestIds, type RailPage } from './railBatches';

export type RailQueryParams = {
  slug: HomeCollectionSlug;
  carousel: PromotedContestCarousel;
  tab: PromotedContestTab;
  userId: string | null;
  locationKey: string;
  firstBatchSize: number;
};

export type RailQueryOptions = {
  queryKey: ReturnType<typeof CondensedCarouselsQueryKeys.CAROUSEL_TAB>;
  queryFn: (context: { pageParam?: number; signal?: AbortSignal }) => Promise<RailPage>;
  getNextPageParam: (lastPage: RailPage) => number | undefined;
  retry: (failureCount: number, error: unknown) => boolean;
  retryDelay: number;
  staleTime: number;
  cacheTime: number;
};

const FIRST_BATCH_OFFSET = 0;

const isHttpClientError = (error: unknown): boolean => {
  if (!axios.isAxiosError(error) || error.response === undefined) return false;

  const { status } = error.response;
  return status >= HttpStatusCode.BadRequest && status < HttpStatusCode.InternalServerError;
};

const isTransientRailError = (error: unknown): boolean =>
  !isHttpClientError(error) && !axios.isCancel(error);

const retryTransientRailError = (failureCount: number, error: unknown): boolean =>
  failureCount < RAIL_RETRY_LIMIT && isTransientRailError(error);

const buildCarouselPayload = (
  carousel: PromotedContestCarousel,
  contestIds: string[]
): GetCarouselContestsPayload => ({
  contestIds,
  includeFull: carousel.showFull,
  includePrivate: carousel.showPrivate,
  includeInProgress: carousel.showInProgress,
});

export const createRailQueryOptions = ({
  slug,
  carousel,
  tab,
  userId,
  locationKey,
  firstBatchSize,
}: RailQueryParams): RailQueryOptions => ({
  queryKey: CondensedCarouselsQueryKeys.CAROUSEL_TAB({
    slug,
    carouselId: carousel.id,
    tabValue: tab.value,
    contestIds: tab.contest_ids,
    showPrivate: carousel.showPrivate,
    showFull: carousel.showFull,
    showInProgress: carousel.showInProgress,
    userId,
    locationKey,
  }),
  queryFn: async ({ pageParam: offset = FIRST_BATCH_OFFSET, signal }) => {
    const batchSize = offset === FIRST_BATCH_OFFSET ? firstBatchSize : NEXT_BATCH_SIZE;
    const batchIds = tab.contest_ids.slice(offset, offset + batchSize);
    const rows = await getCarouselContests(buildCarouselPayload(carousel, batchIds), signal);

    return { offset, requestedCount: batchIds.length, contests: orderByContestIds(batchIds, rows) };
  },
  getNextPageParam: (lastPage) => getNextBatchOffset(lastPage, tab.contest_ids.length),
  retry: retryTransientRailError,
  retryDelay: RAIL_RETRY_DELAY_MS,
  staleTime: STALE_TIME_5_MIN,
  cacheTime: CONDENSED_CAROUSELS_CACHE_TIME,
});
